/**
 * Live Yjs → LG-2849 conversion for the CRDT v2 read path (LG-2850).
 *
 * `src/models/crdtV2/` stays Yjs-free; every `Y.*` read of the v2 document lives here. The functions
 * are offset-preserving: each embed occupies exactly one unit of `XmlText` index space (matching
 * `indexedOpLength`), so the `IndexedStream` / `ObserveDelta` they emit line up with the projection's
 * absolute offset bookkeeping.
 */

import * as Y from 'yjs'

import { ROOT_KEYS } from 'src/models/crdtV2'
import type { IndexedStream, ObserveDelta, ObserveDeltaOp, StreamEmbed } from 'src/models/crdtV2'
import type { TimingsMap } from 'src/models/crdtV2'

/** Minimal projected speaker; `roleId` is intentionally dropped until LG-2864. */
export type SpeakerSnapshot = { id: string; name: string }

/**
 * The root `t` value is an `XmlFragment` wrapping a single `XmlText` (schema spine). Return that
 * text node, creating an empty one when the fragment has no child yet (fresh doc before first sync).
 */
export const getStreamText = (doc: Y.Doc): Y.XmlText => {
    const fragment = doc.get(ROOT_KEYS.text, Y.XmlFragment) as Y.XmlFragment
    const first = fragment.length > 0 ? fragment.get(0) : undefined
    if (first instanceof Y.XmlText) {
        return first
    }

    const text = new Y.XmlText()
    fragment.insert(0, [text])

    return text
}

/**
 * Snapshot one `XmlText` embed into a plain `StreamEmbed`. A live `paragraph_change` is a `Y.Map`
 * (`toJSON()`); plain `utterance_change` / `audit_event` embeds are already dicts (shallow-cloned so
 * the projection never aliases live doc content).
 */
export const snapshotEmbed = (embed: unknown): StreamEmbed => {
    if (embed instanceof Y.AbstractType) {
        return embed.toJSON() as StreamEmbed
    }

    return { ...(embed as Record<string, unknown>) } as StreamEmbed
}

/** Full read of the stream: `XmlText.toDelta()` → offset-preserving `IndexedStream`. */
export const xmlTextToIndexedStream = (text: Y.XmlText): IndexedStream => {
    const delta = text.toDelta() as Array<{ insert: string | object }>

    return delta.map((op) => (typeof op.insert === 'string' ? { insert: op.insert } : { insert: snapshotEmbed(op.insert) }))
}

type YTextDeltaOp = {
    retain?: number
    insert?: string | object
    delete?: number
    attributes?: Record<string, unknown>
}

/** Map a `Y.YTextEvent` delta onto an `ObserveDelta` the incremental layer accepts. */
export const yTextEventDeltaToObserveDelta = (delta: readonly YTextDeltaOp[]): ObserveDelta => {
    const out: ObserveDelta = []

    for (const op of delta) {
        if (typeof op.retain === 'number') {
            const step: ObserveDeltaOp = op.attributes ? { retain: op.retain, attributes: op.attributes } : { retain: op.retain }
            out.push(step)
            continue
        }
        if (typeof op.delete === 'number') {
            out.push({ delete: op.delete })
            continue
        }
        if (typeof op.insert === 'string') {
            out.push({ insert: op.insert })
            continue
        }
        if (op.insert !== undefined) {
            out.push({ insert: snapshotEmbed(op.insert) })
        }
    }

    return out
}

const readTimingPairs = (value: unknown): Array<[number, number]> | null => {
    const pairs = value instanceof Y.Array ? (value.toJSON() as unknown[]) : Array.isArray(value) ? value : null
    if (pairs === null) {
        return null
    }

    const timings: Array<[number, number]> = []
    for (const pair of pairs) {
        if (Array.isArray(pair) && typeof pair[0] === 'number' && typeof pair[1] === 'number') {
            timings.push([pair[0], pair[1]])
        }
    }

    return timings
}

/** `doc["timings"]` (`Map<utteranceId, Array<[start, end]>>`) → plain `TimingsMap`. */
export const timingsMapFromYMap = (map: Y.Map<unknown>): TimingsMap => {
    const out: TimingsMap = {}

    map.forEach((value, utteranceId) => {
        const timings = readTimingPairs(value)
        if (timings !== null) {
            out[utteranceId] = timings
        }
    })

    return out
}

/** One utterance's `[start, end]` pairs, or null when the key is absent / malformed (for incremental updates). */
export const readUtteranceTiming = (map: Y.Map<unknown>, utteranceId: string): Array<[number, number]> | null =>
    map.has(utteranceId) ? readTimingPairs(map.get(utteranceId)) : null

/** `doc["speakers"]` (`Map<speakerId, { name, roleId? }>`) → `{ id, name }` per speaker. */
export const speakersFromYMap = (map: Y.Map<unknown>): Record<string, SpeakerSnapshot> => {
    const out: Record<string, SpeakerSnapshot> = {}

    map.forEach((value, id) => {
        const name = value instanceof Y.Map ? value.get('name') : (value as { name?: unknown } | null)?.name
        out[id] = { id, name: typeof name === 'string' ? name : '' }
    })

    return out
}

/**
 * `doc["recordingStartedAt"]` is a single-element `Y.Array`; the unix-seconds anchor is index `0`.
 * Empty, missing, or non-number → `0` (matches `Transcript.EMPTY`).
 */
export const recordingStartedAtFromYArray = (array: Y.Array<unknown>): number => {
    const value = array.length > 0 ? array.get(0) : undefined

    return typeof value === 'number' && Number.isFinite(value) ? value : 0
}
